import React, { useState, useEffect, useMemo } from "react";

import {
  createManualJournalEntry,
  getChartOfAccounts,
} from "utils/api/private/Accounting.api";
import { extractErrorMessage } from "utils/helper/ApiResponses";
import {
  Account,
  JournalEntry,
  JournalLine,
  operationType,
} from "utils/types/Accounting.types";

// MUI Imports
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Divider,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  SelectChangeEvent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { authToken } from "utils/constant"; // Import your authToken key


const initialLine: Omit<JournalLine, "amount"> & { amount: string } = {
  accountId: "",
  type: "Debit",
  amount: "0",
  description: "",
};

export const ManualJournalEntryForm = () => {
  
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [lines, setLines] = useState([
    initialLine,
    { ...initialLine, type: "Credit" },
  ]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const fetchAccounts = async () => {
      // Retrieve the token from localStorage or your auth state management
      const token = localStorage.getItem(authToken);
      if (!token) {
        setError("Authentication token not found. Please log in again.");
        return;
      }
      try {
        const fetchedAccounts = await getChartOfAccounts(token);
        setAccounts(fetchedAccounts);
      } catch (err) {
        setError("Could not load chart of accounts. Please try again later.");
      }
    };
    fetchAccounts();
  }, []);

  const { totalDebits, totalCredits, isBalanced } = useMemo(() => {
    const totals = lines.reduce(
      (acc, line) => {
        const amount = parseFloat(line.amount) || 0;
        if (line.type === "Debit") acc.debits += amount;
        else acc.credits += amount;
        return acc;
      },
      { debits: 0, credits: 0 }
    );
    return {
      totalDebits: totals.debits,
      totalCredits: totals.credits,
      isBalanced:
        Math.abs(totals.debits - totals.credits) < 0.001 && totals.debits > 0,
    };
  }, [lines]);

  const handleLineChange = (
    index: number,
    field: keyof typeof initialLine,
    value: string | operationType
  ) => {
    const newLines = [...lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setLines(newLines);
  };

  const handleSelectChange =
    (index: number, field: keyof typeof initialLine) =>
    (event: SelectChangeEvent<string>) => {
      handleLineChange(index, field, event.target.value);
    };

  const addLine = () => setLines([...lines, initialLine]);

  const removeLine = (index: number) => {
    if (lines.length > 2) {
      setLines(lines.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isBalanced) {
      setError("Entry is not balanced. Total debits must equal total credits.");
      return;
    }
    setError(null);
    setIsLoading(true);
    const token = localStorage.getItem(authToken);
    if (!token) {
      setError("Authentication failed. Please log in again.");
      setIsLoading(false);
      return;
    }

    try {
      const entryPayload: Omit<JournalEntry, "id"> = {
        timestamp: new Date(date).getTime(),
        description,
        lines: lines.map((line) => ({
          accountId: line.accountId,
          description: line.description,
          amount: parseFloat(line.amount),
          // 👇 Add the type assertion here
          type: line.type as operationType,
        })),
      };

      await createManualJournalEntry(entryPayload, token);
      setSuccess("Journal entry created successfully!");
      // Reset form on success
      setDescription("");
      setDate(new Date().toISOString().split("T")[0]);
      setLines([initialLine, { ...initialLine, type: "Credit" }]);
    } catch (err) {
      const { message } = extractErrorMessage(err);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 4, mt: 4 }}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Typography variant="h5" component="h2" gutterBottom>
            Create Manual Journal Entry
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <TextField
              type="date"
              label="Date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Paid monthly server costs"
              required
              fullWidth
            />
          </Stack>

          <Divider sx={{ my: 2 }} />

          {lines.map((line, index) => (
            <Grid
              container
              spacing={2}
              key={index}
              sx={{ mb: 2 }}
              alignItems="center"
            >
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel id={`account-label-${index}`}>Account</InputLabel>
                  <Select
                    labelId={`account-label-${index}`}
                    label="Account"
                    value={line.accountId}
                    onChange={handleSelectChange(index, "accountId")}
                  >
                    {accounts.map((acc) => (
                      <MenuItem key={acc.id} value={acc.id}>
                        {acc.id} - {acc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Line Description"
                  value={line.description}
                  onChange={(e) =>
                    handleLineChange(index, "description", e.target.value)
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  type="number"
                  label="Amount"
                  value={line.amount}
                  onChange={(e) =>
                    handleLineChange(index, "amount", e.target.value)
                  }
                  required
                  fullWidth
                  inputProps={{ min: "0", step: "0.01" }}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth>
                  <InputLabel id={`type-label-${index}`}>Type</InputLabel>
                  <Select
                    labelId={`type-label-${index}`}
                    label="Type"
                    value={line.type}
                    onChange={handleSelectChange(index, "type")}
                  >
                    <MenuItem value="Debit">Debit</MenuItem>
                    <MenuItem value="Credit">Credit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={1}>
                <IconButton
                  onClick={() => removeLine(index)}
                  disabled={lines.length <= 2}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Button startIcon={<AddIcon />} onClick={addLine} sx={{ mb: 2 }}>
            Add Line
          </Button>

          <Divider sx={{ my: 2 }} />

          <Stack
            direction="row"
            spacing={3}
            sx={{ my: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}
          >
            <Typography>
              Total Debits: <strong>${totalDebits.toFixed(2)}</strong>
            </Typography>
            <Typography>
              Total Credits: <strong>${totalCredits.toFixed(2)}</strong>
            </Typography>
            <Typography
              color={isBalanced ? "success.main" : "error.main"}
              fontWeight="bold"
            >
              {isBalanced ? "✔ Balanced" : "✖ Not Balanced"}
            </Typography>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 3, position: "relative" }}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={!isBalanced || isLoading}
              size="large"
            >
              Submit Entry
            </Button>
            {isLoading && (
              <CircularProgress
                size={24}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Box>
        </Box>
      </Paper>
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert
          onClose={() => setSuccess(null)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};
